import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import PeriodForm from '../../../components/Forms/PeriodForm';
import { periodService } from '../../../services/apiService';
import { showToast } from '../../../utils/toast';

const formatPeriodTime = (time) => {
  if (!time) return '—';
  const parts = time.split(':');
  const hour = parseInt(parts[0], 10);
  const minute = parts[1] ?? '00';
  if (Number.isNaN(hour)) return time;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const normalizedHour = ((hour + 11) % 12) + 1;
  return `${String(normalizedHour).padStart(2, '0')}:${minute} ${suffix}`;
};

const PeriodsTab = ({ onAddRef }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadPeriods = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await periodService.getAll();
      const periods = res?.data || res || [];
      setPeriods(Array.isArray(periods) ? periods : []);
    } catch (err) {
      console.error('Error loading periods:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to load periods');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPeriods();
  }, []);

  const handleAdd = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  useEffect(() => {
    if (onAddRef) {
      onAddRef(handleAdd);
    }
  }, [handleAdd, onAddRef]);

  const handleEdit = (row) => {
    setEditing(row);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleModalSave = async (formData) => {
    try {
      setModalLoading(true);
      if (editing) {
        await periodService.update(editing.id, formData);
        showToast.success('Period updated successfully');
      } else {
        await periodService.create(formData);
        showToast.success('Period created successfully');
      }
      await loadPeriods();
      handleModalClose();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to save period';
      showToast.error(message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete period "${row.period_name}"?`)) return;
    try {
      setLoading(true);
      await periodService.delete(row.id);
      showToast.success('Period deleted successfully');
      await loadPeriods();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to delete period';
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1.5, md: 2 }, 
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
    }}>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : periods.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No periods configured
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: { xs: 1.5, sm: 1.75 },
          }}
        >
          {periods.map((period) => (
            <Paper
              key={period.id}
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 2.4 },
                borderRadius: 2,
                borderColor: 'divider',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 2,
                  borderColor: 'primary.main',
                },
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                    {period.period_name || '—'}
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Start:</strong> {formatPeriodTime(period.start_time)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>End:</strong> {formatPeriodTime(period.end_time)}
                    </Typography>
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(period)}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(period)}
                    sx={{
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'error.dark',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      <PeriodForm
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSave}
        initialData={editing}
        loading={modalLoading}
        title={editing ? 'Edit Period' : 'Add Period'}
      />
    </Box>
  );
};

export default PeriodsTab;
